<template>
  <div
    class="instruction-package-detail"
    data-testid="instruction-package-detail-page"
  >
    <WorkInstructionsHeader
      :hide-package-dropdown="true"
      :hide-actions="true"
      :custom-breadcrumbs="currentInstructionPackageName"
      :force-product-list-text="$t('product_template_list')"
      :back-to-work-instructions-list="true"
      :back-to-list-route-name="'editor-instruction-packages-list'"
      :back-to-list-label-key="'work_instructions.instruction_packages.label'"
    />

    <div class="wrapper" v-if="currentPackage">
      <WIStepModal
        v-if="showAddWIStepModal"
        :initialData="selectedStepForEdit"
        :isEditMode="isEditingWorkInstructionStep"
        @close="showAddWIStepModal = false"
        @save="onSaveWorkInstructionStep"
        @onDelete="onDeleteWorkInstructionStep"
      />

      <div class="top-actions" data-testid="instruction-package-top-actions">
        <button
          class="link-wi-button"
          data-testid="instruction-package-link-wi-btn"
          @click="openLinkModal"
        >
          <b-icon icon="plus"></b-icon>
          {{ $t('work_instructions.instruction_packages.link_button') }}
        </button>
      </div>

      <div
        v-if="linkedWorkInstructions.length === 0"
        class="empty-state"
        data-testid="instruction-package-empty-state"
      >
        {{ $t('work_instructions.instruction_packages.empty_linked') }}
      </div>

      <div v-if="linkedWorkInstructions.length > 0" class="items-host">
        <InstructionCardsList
          :instructions="linkedWorkInstructions"
          :expanded-instructions="expandedInstructions"
          :show-status-badge="false"
          :show-add-step-button="false"
          :enable-step-click="false"
          :enable-instruction-reorder="true"
          container-test-id="instruction-package-items-container"
          card-test-id="instruction-package-wi-card"
          step-card-test-id="instruction-package-wi-step-card"
          @toggle="onToggleInstructionEvent"
          @edit-step="onEditWorkInstructionStepEvent"
          @reorder-instructions="onReorderLinkedInstructions"
        />
      </div>
    </div>

    <div v-else class="wrapper">
      <div
        class="empty-state"
        data-testid="instruction-package-not-found-state"
      >
        {{ $t('work_instructions.instruction_packages.not_found') }}
      </div>
    </div>

    <b-modal
      :active="showLinkModal"
      has-modal-card
      trap-focus
      @close="showLinkModal = false"
    >
      <div
        class="modal-card link-wi-modal"
        data-testid="instruction-package-link-modal"
      >
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ $t('work_instructions.instruction_packages.modal.title') }}
          </p>
        </header>
        <section class="modal-card-body link-wi-modal-body">
          <div class="link-wi-table-wrap">
            <table class="link-wi-table">
              <thead>
                <tr>
                  <th class="select-column"></th>
                  <th>
                    {{
                      $t(
                        'work_instructions.instruction_packages.modal.columns.name'
                      )
                    }}
                  </th>
                  <th>
                    {{
                      $t(
                        'work_instructions.instruction_packages.modal.columns.description'
                      )
                    }}
                  </th>
                  <th>
                    {{
                      $t(
                        'work_instructions.instruction_packages.modal.columns.linked_instruction_packages'
                      )
                    }}
                  </th>
                  <th>
                    {{
                      $t(
                        'work_instructions.instruction_packages.modal.columns.linked_tasks'
                      )
                    }}
                  </th>
                  <th>
                    {{
                      $t(
                        'work_instructions.instruction_packages.modal.columns.linked_templates'
                      )
                    }}
                  </th>
                  <th class="open-column">
                    {{
                      $t(
                        'work_instructions.instruction_packages.modal.columns.open_wi'
                      )
                    }}
                  </th>
                </tr>
                <tr class="search-row">
                  <th></th>
                  <th>
                    <input
                      v-model="linkFilters.name"
                      class="column-search-input"
                      type="text"
                      :placeholder="
                        $t(
                          'work_instructions.instruction_packages.modal.search_placeholder'
                        )
                      "
                    />
                  </th>
                  <th>
                    <input
                      v-model="linkFilters.description"
                      class="column-search-input"
                      type="text"
                      :placeholder="
                        $t(
                          'work_instructions.instruction_packages.modal.search_placeholder'
                        )
                      "
                    />
                  </th>
                  <th>
                    <input
                      v-model="linkFilters.linkedInstructionPackages"
                      class="column-search-input"
                      type="text"
                      :placeholder="
                        $t(
                          'work_instructions.instruction_packages.modal.search_placeholder'
                        )
                      "
                    />
                  </th>
                  <th>
                    <input
                      v-model="linkFilters.linkedTasks"
                      class="column-search-input"
                      type="text"
                      :placeholder="
                        $t(
                          'work_instructions.instruction_packages.modal.search_placeholder'
                        )
                      "
                    />
                  </th>
                  <th>
                    <input
                      v-model="linkFilters.linkedTemplates"
                      class="column-search-input"
                      type="text"
                      :placeholder="
                        $t(
                          'work_instructions.instruction_packages.modal.search_placeholder'
                        )
                      "
                    />
                  </th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="instructionPackage in filteredAvailableWorkInstructionPackages"
                  :key="instructionPackage._id"
                >
                  <td class="select-column">
                    <input
                      type="checkbox"
                      :checked="isPackageSelected(instructionPackage._id)"
                      @change="togglePackageSelection(instructionPackage._id)"
                    />
                  </td>
                  <td>{{ instructionName(instructionPackage) }}</td>
                  <td>{{ instructionDescription(instructionPackage) }}</td>
                  <td>{{ instructionLinkedPackages(instructionPackage) }}</td>
                  <td>{{ instructionLinkedTasks(instructionPackage) }}</td>
                  <td>{{ instructionLinkedTemplates(instructionPackage) }}</td>
                  <td class="open-column">
                    <button
                      class="open-wi-btn"
                      type="button"
                      @click="openWorkInstructionEditor(instructionPackage)"
                    >
                      <b-icon icon="open-in-new" />
                    </button>
                  </td>
                </tr>

                <tr
                  v-if="filteredAvailableWorkInstructionPackages.length === 0"
                >
                  <td colspan="7" class="empty-row">
                    {{
                      $t(
                        'work_instructions.instruction_packages.modal.empty_available'
                      )
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <footer class="modal-card-foot link-wi-modal-footer">
          <b-button type="is-danger" outlined @click="showLinkModal = false">
            {{ $t('cancel') }}
          </b-button>
          <b-button
            type="is-success"
            :disabled="selectedPackageIds.length === 0"
            @click="onLinkSelectedPackages"
          >
            {{ $t('work_instructions.instruction_packages.modal.link_action') }}
          </b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { v4 } from 'uuid';
import client from '../../config/api';
import WorkInstructionsHeader from '../Shared/WorkInstructions/Header';
import WIStepModal from '../Shared/WorkInstructions/WIStepModal';
import InstructionCardsList from '../Shared/WorkInstructions/InstructionCardsList';

const uuid = v4;

export default {
  name: 'editor-instruction-package-detail',
  components: {
    WorkInstructionsHeader,
    WIStepModal,
    InstructionCardsList,
  },
  data() {
    return {
      isLoading: false,
      currentPackage: null,
      allWorkInstructionPackages: [],
      linkedWorkInstructions: [],
      availableWorkInstructionPackages: [],
      showLinkModal: false,
      linkFilters: {
        name: '',
        description: '',
        linkedInstructionPackages: '',
        linkedTasks: '',
        linkedTemplates: '',
      },
      selectedPackageIds: [],
      expandedInstructions: {},
      isReorderingInstructions: false,
      showAddWIStepModal: false,
      instructionIdToEdit: null,
      selectedStepForEdit: null,
    };
  },
  watch: {
    '$route.params.packageId': {
      immediate: true,
      async handler() {
        await this.loadView();
      },
    },
  },
  computed: {
    currentInstructionPackageName() {
      return this.currentPackage?.title || '';
    },
    isEditingWorkInstructionStep() {
      return !!this.selectedStepForEdit;
    },
    filteredAvailableWorkInstructionPackages() {
      const filters = {
        name: this.normalizeId(this.linkFilters.name).toLowerCase(),
        description: this.normalizeId(
          this.linkFilters.description
        ).toLowerCase(),
        linkedInstructionPackages: this.normalizeId(
          this.linkFilters.linkedInstructionPackages
        ).toLowerCase(),
        linkedTasks: this.normalizeId(
          this.linkFilters.linkedTasks
        ).toLowerCase(),
        linkedTemplates: this.normalizeId(
          this.linkFilters.linkedTemplates
        ).toLowerCase(),
      };

      return (this.availableWorkInstructionPackages || []).filter(
        (instructionPackage) => {
          const matchesName = this.instructionName(instructionPackage)
            .toLowerCase()
            .includes(filters.name);
          const matchesDescription = this.instructionDescription(
            instructionPackage
          )
            .toLowerCase()
            .includes(filters.description);
          const matchesLinkedPackages = this.instructionLinkedPackages(
            instructionPackage
          )
            .toLowerCase()
            .includes(filters.linkedInstructionPackages);
          const matchesLinkedTasks = this.instructionLinkedTasks(
            instructionPackage
          )
            .toLowerCase()
            .includes(filters.linkedTasks);
          const matchesLinkedTemplates = this.instructionLinkedTemplates(
            instructionPackage
          )
            .toLowerCase()
            .includes(filters.linkedTemplates);

          return (
            matchesName &&
            matchesDescription &&
            matchesLinkedPackages &&
            matchesLinkedTasks &&
            matchesLinkedTemplates
          );
        }
      );
    },
  },
  methods: {
    normalizeId(value) {
      if (value == null) return '';
      return String(value).trim();
    },
    packageRouteIds(wiPackage) {
      return [wiPackage?.uuid, wiPackage?.id, wiPackage?._id]
        .map((value) => this.normalizeId(value))
        .filter(Boolean);
    },
    packageInstructionIds(wiPackage) {
      const fromInstructions = (wiPackage?.instructions || [])
        .map((instruction) => this.normalizeId(instruction?._id))
        .filter(Boolean);

      const fromWorkInstructions = (wiPackage?.workInstructions || [])
        .map((id) => this.normalizeId(id))
        .filter(Boolean);

      return Array.from(
        new Set([...fromInstructions, ...fromWorkInstructions])
      );
    },
    packageInstructionItems(wiPackage) {
      return (wiPackage?.instructions || []).map((instruction) => ({
        ...instruction,
        sourcePackageId: wiPackage._id,
        sourcePackageTitle: wiPackage.title,
        sourcePackageType: wiPackage.type,
      }));
    },
    async loadLinkableWorkInstructionPackages() {
      try {
        const response = await client.get(
          '/work-instructions-package/link-candidates'
        );
        console.debug(
          'loadLinkableWorkInstructionPackages: response',
          response
        );
        this.allWorkInstructionPackages = response?.data || [];
      } catch (err) {
        console.error('Failed to load linkable work instruction packages', err);
        this.allWorkInstructionPackages = [];
      }
    },
    mapLinkedState() {
      const linkedInstructionIds = (this.currentPackage?.workInstructions || [])
        .map((value) => this.normalizeId(value))
        .filter(Boolean);
      const linkedIds = new Set(linkedInstructionIds);
      const rawInstructionItems = (
        this.allWorkInstructionPackages || []
      ).flatMap((wiPackage) => this.packageInstructionItems(wiPackage));

      const instructionById = new Map();
      rawInstructionItems.forEach((instruction) => {
        const instructionId = this.normalizeId(instruction?._id);
        if (!instructionId) {
          return;
        }

        const existingInstruction = instructionById.get(instructionId);
        const isExistingFromWip =
          existingInstruction?.sourcePackageType ===
          'standard_instruction_package';
        const isCurrentFromWiPackage =
          instruction?.sourcePackageType !== 'standard_instruction_package';

        if (
          !existingInstruction ||
          (isExistingFromWip && isCurrentFromWiPackage)
        ) {
          instructionById.set(instructionId, instruction);
        }
      });

      const allInstructionItems = Array.from(instructionById.values());

      // Render strictly from linked ids so UI matches currentPackage.workInstructions.
      this.linkedWorkInstructions = linkedInstructionIds
        .map((instructionId) => instructionById.get(instructionId))
        .filter(Boolean);

      // Link modal must list WI only (never WIP rows).
      this.availableWorkInstructionPackages = allInstructionItems.filter(
        (instruction) => !linkedIds.has(this.normalizeId(instruction?._id))
      );
    },
    async loadView() {
      this.isLoading = true;
      try {
        const { packageId } = this.$route.params;
        const response = await client.get('/work-instructions-package');
        const packages = response?.data || [];
        const normalizedPackageId = this.normalizeId(packageId);

        const selectedPackage = packages.find(
          (wiPackage) =>
            wiPackage.type === 'standard_instruction_package' &&
            this.packageRouteIds(wiPackage).includes(normalizedPackageId)
        );

        this.currentPackage = selectedPackage || null;

        console.debug('loadView: selectedPackage', this.currentPackage);

        if (!this.currentPackage) {
          this.allWorkInstructionPackages = [];
          this.linkedWorkInstructions = [];
          this.availableWorkInstructionPackages = [];
          return;
        }

        await this.loadLinkableWorkInstructionPackages();
        this.mapLinkedState();
      } finally {
        this.isLoading = false;
      }
    },
    openLinkModal() {
      this.selectedPackageIds = [];
      this.linkFilters = {
        name: '',
        description: '',
        linkedInstructionPackages: '',
        linkedTasks: '',
        linkedTemplates: '',
      };
      this.showLinkModal = true;
    },
    async onLinkSelectedPackages() {
      if (!this.currentPackage || this.selectedPackageIds.length === 0) {
        return;
      }

      const allInstructionIds = new Set(
        (this.allWorkInstructionPackages || [])
          .flatMap((wiPackage) => this.packageInstructionItems(wiPackage))
          .map((instruction) => this.normalizeId(instruction?._id))
          .filter(Boolean)
      );

      const linkedIds = new Set(
        (this.currentPackage?.workInstructions || [])
          .map((value) => this.normalizeId(value))
          .filter((instructionId) => allInstructionIds.has(instructionId))
      );

      (this.availableWorkInstructionPackages || [])
        .filter((instruction) => this.isPackageSelected(instruction?._id))
        .forEach((instruction) => {
          linkedIds.add(this.normalizeId(instruction?._id));
        });

      const payload = {
        type: 'standard_instruction_package',
        workInstructions: Array.from(linkedIds),
      };

      console.debug(
        `onLinkSelectedPackages: PUT /work-instructions-package/${this.currentPackage._id}`,
        payload
      );
      const putResponse = await client.put(
        `/work-instructions-package/${this.currentPackage._id}`,
        payload
      );
      console.debug('onLinkSelectedPackages: putResponse', putResponse);

      this.showLinkModal = false;
      this.selectedPackageIds = [];
      await this.loadView();
    },
    async onReorderLinkedInstructions({ fromIndex, toIndex }) {
      if (
        !this.currentPackage ||
        this.isReorderingInstructions ||
        fromIndex === toIndex
      ) {
        return;
      }

      const items = [...(this.linkedWorkInstructions || [])];
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= items.length ||
        toIndex >= items.length
      ) {
        return;
      }

      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);

      const orderedInstructionIds = items
        .map((instruction) => this.normalizeId(instruction?._id))
        .filter(Boolean);

      const previousItems = this.linkedWorkInstructions;
      const previousIds = this.currentPackage?.workInstructions || [];

      this.linkedWorkInstructions = items;
      this.currentPackage = {
        ...this.currentPackage,
        workInstructions: orderedInstructionIds,
      };

      this.isReorderingInstructions = true;

      try {
        const payload = {
          type: 'standard_instruction_package',
          workInstructions: orderedInstructionIds,
        };

        console.debug(
          `onReorderLinkedInstructions: PUT /work-instructions-package/${this.currentPackage._id}`,
          payload
        );

        await client.put(
          `/work-instructions-package/${this.currentPackage._id}`,
          payload
        );
      } catch (error) {
        console.error('Failed to reorder linked work instructions', error);

        this.linkedWorkInstructions = previousItems;
        this.currentPackage = {
          ...this.currentPackage,
          workInstructions: previousIds,
        };
      } finally {
        this.isReorderingInstructions = false;
      }
    },
    isPackageSelected(packageId) {
      const normalizedId = this.normalizeId(packageId);
      return this.selectedPackageIds.some(
        (selectedId) => this.normalizeId(selectedId) === normalizedId
      );
    },
    togglePackageSelection(packageId) {
      const normalizedId = this.normalizeId(packageId);
      const selectedSet = new Set(
        this.selectedPackageIds.map((value) => this.normalizeId(value))
      );

      if (selectedSet.has(normalizedId)) {
        selectedSet.delete(normalizedId);
      } else {
        selectedSet.add(normalizedId);
      }

      this.selectedPackageIds = Array.from(selectedSet);
    },
    instructionName(instruction) {
      return instruction?.title || instruction?.name || '-';
    },
    instructionDescription(instruction) {
      return instruction?.description || '-';
    },
    instructionLinkedPackages(instruction) {
      const linkedPackages = instruction?.linkedInstructionPackages;

      if (Array.isArray(linkedPackages) && linkedPackages.length > 0) {
        return linkedPackages.join(', ');
      }

      return instruction?.sourcePackageTitle || '-';
    },
    instructionLinkedTasks(instruction) {
      const linkedTasks = instruction?.linkedTasks;

      if (Array.isArray(linkedTasks) && linkedTasks.length > 0) {
        return linkedTasks
          .map((task) => task?.title || task?.name || task)
          .filter(Boolean)
          .join(', ');
      }

      return '[...]';
    },
    instructionLinkedTemplates(instruction) {
      const linkedTemplates = instruction?.linkedTemplates;

      if (Array.isArray(linkedTemplates) && linkedTemplates.length > 0) {
        return linkedTemplates
          .map((template) => template?.title || template?.name || template)
          .filter(Boolean)
          .join(', ');
      }

      return '[...]';
    },
    openWorkInstructionEditor(instruction) {
      const routePackageId =
        this.normalizeId(instruction?.sourcePackageId) ||
        this.normalizeId(instruction?._id);

      if (!routePackageId) {
        return;
      }

      this.$router.push({
        name: 'editor-work-instructions-package',
        params: { packageId: routePackageId },
      });
    },
    onToggleInstruction(instructionId, isOpen) {
      this.$set(this.expandedInstructions, instructionId, isOpen);
    },
    onToggleInstructionEvent({ instructionId, isOpen }) {
      this.onToggleInstruction(instructionId, isOpen);
    },
    onShowAddWIStepModal(instructionId) {
      this.instructionIdToEdit = instructionId;
      this.selectedStepForEdit = null;
      this.showAddWIStepModal = true;
    },
    onEditWorkInstructionStep(step, instructionId) {
      this.instructionIdToEdit = instructionId;
      this.selectedStepForEdit = {
        ...step,
      };
      this.showAddWIStepModal = true;
    },
    onEditWorkInstructionStepEvent({ step, instructionId }) {
      this.onEditWorkInstructionStep(step, instructionId);
    },
    async onSaveWorkInstructionStep(stepToAdd) {
      const instructionId = this.instructionIdToEdit;
      this.showAddWIStepModal = false;

      if (!instructionId) {
        return;
      }

      if (this.isEditingWorkInstructionStep) {
        await client.put(
          `/work-instructions/step/${instructionId}/${this.selectedStepForEdit._id}`,
          {
            ...this.selectedStepForEdit,
            ...stepToAdd,
          }
        );

        this.selectedStepForEdit = null;
        this.$set(this.expandedInstructions, instructionId, true);
        await this.loadView();
        return;
      }

      await client.post(`/work-instructions/step/${instructionId}`, {
        _id: uuid(),
        description: stepToAdd?.description || '',
      });

      this.$set(this.expandedInstructions, instructionId, true);
      await this.loadView();
    },
    async onDeleteWorkInstructionStep() {
      const instructionId = this.instructionIdToEdit;
      const stepId = this.selectedStepForEdit?._id;
      this.showAddWIStepModal = false;

      if (!instructionId || !stepId) {
        return;
      }

      await client.delete(`/work-instructions/step/${instructionId}/${stepId}`);
      this.selectedStepForEdit = null;
      this.$set(this.expandedInstructions, instructionId, true);
      await this.loadView();
    },
  },
};
</script>

<style scoped lang="scss">
.wrapper {
  min-height: calc(100vh - 100px);
  padding: 20px 10px;
  background-color: #dddddd;
  display: flex;
  flex-direction: column;
}

.top-actions {
  width: 100%;
  background: #efefef;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.link-wi-button {
  border: none;
  background: transparent;
  font-family: 'Siemens Sans';
  font-size: 22px;
  font-weight: 700;
  color: #0f2e54;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  line-height: 1.2;
}

.link-wi-button :deep(.icon) {
  font-size: 18px;
}

.items-host {
  flex: 1;
  overflow: hidden;
}

.empty-state {
  margin: auto;
  font-size: 18px;
  color: #555;
  text-align: center;
}

.link-wi-modal {
  width: min(1200px, 95vw);
}

.link-wi-modal-body {
  padding: 0;
}

.link-wi-table-wrap {
  max-height: 55vh;
  overflow: auto;
}

.link-wi-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    padding: 12px 10px;
    border-bottom: 1px solid #dbdbdb;
    text-align: left;
    vertical-align: middle;
  }

  th {
    font-weight: 700;
    color: #2f2f2f;
    background: #fff;
    position: sticky;
    top: 0;
    z-index: 1;
  }
}

.search-row th {
  background: #fff;
  position: sticky;
  top: 45px;
  z-index: 1;
}

.column-search-input {
  width: 100%;
  border: 1px solid #d7d7d7;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 13px;
}

.select-column {
  width: 40px;
}

.open-column {
  width: 90px;
  text-align: center !important;
}

.open-wi-btn {
  border: 1px solid #a7a7a7;
  background: #fff;
  border-radius: 3px;
  width: 30px;
  height: 30px;
  cursor: pointer;
}

.empty-row {
  text-align: center !important;
  color: #666;
  padding: 24px 8px !important;
}

.link-wi-modal-footer {
  justify-content: space-between;
}
</style>
